// src/components/SubscriptionPage.jsx
import React, { useState, useEffect } from "react";
import { getPlans, createStripeSubscription, getSubscriptionStatus, createFreeSubscription, createPreviewSubscription, getCreditPacks, purchaseCredits } from "../utils/api";
import { useAuthContext } from "../contexts/AuthContext";
import { useError } from "../contexts/ErrorContext";
import AuthModal from "./AuthModal";
import { useNavigate, useLocation } from "react-router-dom";
import fbPixel from "../utils/fbPixel";
import SEO from "./SEO";

const UNLIMITED = 999999;

// Common features list to be used in both SubscriptionPage and ManageSubscriptionPage
export const PREMIUM_FEATURES = [
  "Send unlimited greeting cards",
  "Unlimited access to all premium templates",
  "No ads, no watermarks – ever",
  "20 AI-generated photos/month",
  "Schedule cards in advance",
  "Unlimited collaborators per card",
];

// Helper function to format feature value
const formatValue = (value) => {
  if (value === UNLIMITED) return "Unlimited";
  if (value === 0) return "No";
  return value;
};

// Helper function to format feature text
const formatFeatureText = (key, value) => {
  const featureTexts = {
    cards_per_month: (val) => `${formatValue(val)} cards per month`,
    template_limit: (val) => `${formatValue(val)} templates`,
    ai_photos_per_month: (val) => `${formatValue(val)} AI-generated photos per month`,
    collaborators_per_card: (val) => `${formatValue(val)} collaborators per card`,
    watermark: (val) => `${val ? 'Has watermark' : 'No watermark'}`,
    can_send: (val) => `${val ? 'Send cards via email' : 'Cannot send cards'}`,
    can_collaborate: (val) => `${val ? 'Invite collaborators' : 'No collaborators'}`,
    custom_branding: (val) => `${val ? 'Custom branding' : 'No custom branding'}`,
    can_schedule: (val) => `${val ? 'Can schedule cards' : 'No card scheduling'}`,
    advanced_customization: (val) => `${val ? 'Advanced customization' : 'Basic customization'}`,
    priority_support: (val) => `${val ? 'Priority support' : 'Standard support'}`,
    priority_features: (val) => `${val ? 'Early access to new features' : 'Standard feature access'}`
  };

  return featureTexts[key]?.(value) || '';
};

export default function SubscriptionPage() {
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [creditPacks, setCreditPacks] = useState([]);
  const [billingPeriod, setBillingPeriod] = useState('yearly'); // 'monthly' or 'yearly'
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedCreditPack, setSelectedCreditPack] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [isPreviewUser, setIsPreviewUser] = useState(false); // Track if user is on Preview tier (legacy users only)
  const { user } = useAuthContext();
  const { showError } = useError();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if coming from template selection or feature requirement
  const isFromTemplate = location.state?.fromTemplate;
  const isFromFeature = location.state?.fromFeature;
  // eslint-disable-next-line no-unused-vars
  const templateMessage = location.state?.message;
  const templateData = location.state?.templateData;
  
  // Initial loading state - true when user is logged in and we need to check their subscription
  // This prevents the pricing page from flashing before redirect
  const [checkingSubscription, setCheckingSubscription] = useState(!!user);
  
  // Check URL params for checkout status and view mode
  const searchParams = new URLSearchParams(location.search);
  const checkoutCanceled = searchParams.get('checkout') === 'canceled';
  const creditsCanceled = searchParams.get('credits') === 'canceled';

  // Scroll to top when page loads and track ViewContent
  useEffect(() => {
    window.scrollTo(0, 0);
    fbPixel.trackContentView('Pricing Page', 'conversion_page');
  }, []);

  // Check subscription status when user logs in
  useEffect(() => {
    async function checkSubscription() {
      if (user) {
        try {
          const { data } = await getSubscriptionStatus();
          // Check if user has an active paid subscription (not preview)
          const isPaidPlan = data?.active && data?.plan_tier && data.plan_tier !== 'preview';
          
          if (isPaidPlan) {
            // If user already has active paid subscription and came from template, go to editing
            if (isFromTemplate && templateData) {
              const category = templateData.category || "General";
              const templateId = templateData.id || "1";
              navigate(`/edit/${category}/${templateId}`, {
                state: {
                  src: templateData.src,
                  category,
                  cardSize: templateData.cardSize || "1024x1024",
                  isVideo: templateData.isVideo || false,
                  mediaType: templateData.mediaType || "image",
                  firebaseUrl: templateData.firebaseUrl || null
                }
              });
              return; // Don't set checkingSubscription to false since we're navigating away
            } else {
              navigate("/manage");
              return; // Don't set checkingSubscription to false since we're navigating away
            }
          } else if (data?.active && data?.plan_tier === 'preview') {
            // User has active Preview subscription
            setIsPreviewUser(true);
          } else {
            // User is logged in but has no active subscription = new user, show preview option
            setIsPreviewUser(false);
          }
        } catch (err) {
          console.error("Failed to check subscription status:", err);
          // If we can't fetch status but user is logged in, show preview option
          setIsPreviewUser(false);
        }
        // Done checking - show the page
        setCheckingSubscription(false);
      } else {
        setIsPreviewUser(false);
        setCheckingSubscription(false);
      }
    }
    checkSubscription();
  }, [user, navigate, isFromTemplate, templateData]);

  // Fetch available Stripe prices/plans and credit packs from the backend
  useEffect(() => {
    async function fetchPlansAndCredits() {
      try {
        const [plansResponse, creditsResponse] = await Promise.all([
          getPlans(),
          getCreditPacks().catch(err => {
            console.warn("Credit packs not available:", err);
            return { data: [] };
          })
        ]);
        setPlans(plansResponse.data);
        setCreditPacks(creditsResponse.data || []);
      } catch (err) {
        console.error("Failed to load plans:", err);
        showError("Could not load subscription plans. Please try again.");
      }
    }
    fetchPlansAndCredits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSubscribe(plan) {
    // Handle the Free plan (no Stripe prices)
    if (!plan.prices?.length) {
      if (!user) {
        // Ask the user to log in / sign up first
        setSelectedPlan(plan);
        setShowAuthModal(true);
      } else {
        // Create free subscription and navigate appropriately
        try {
          setLoading(true);
          await createFreeSubscription();
          
          // Track free plan subscription
          fbPixel.trackSubscription('Free Plan', 0, 'USD');
          
          // Check if we came from template selection and should redirect to editing
          if (isFromTemplate && templateData) {
            const category = templateData.category || "General";
            const templateId = templateData.id || "1";
            console.log('[Free Plan] Redirecting to template editing:', { category, templateId, isVideo: templateData.isVideo });
            navigate(`/edit/${category}/${templateId}`, {
              state: {
                src: templateData.src,
                category,
                cardSize: templateData.cardSize || "1024x1024",
                isVideo: templateData.isVideo || false,
                mediaType: templateData.mediaType || "image",
                firebaseUrl: templateData.firebaseUrl || null,
                fromSubscription: true
              }
            });
          } else {
            // Normal free subscription - go to manage page
            navigate("/manage");
          }
        } catch (err) {
          console.error("Failed to create free subscription:", err);
          showError("Failed to start free plan. Please try again.");
        } finally {
          setLoading(false);
        }
      }
      return;
    }
    
    // If user is not authenticated, show auth modal and save selected plan
    if (!user) {
      setSelectedPlan(plan);
      setShowAuthModal(true);
      return;
    }
    
    // If user is authenticated, proceed with subscription
    await processSubscription(plan);
  }

  async function processSubscription(plan) {
    setLoading(true);
    try {
      // Use selected billing period
      const price = plan.prices.find(p => p.interval === billingPeriod);
      if (!price) {
        throw new Error(`No ${billingPeriod} price found for ${plan.name}`);
      }
      
      // Determine return URL based on context (add checkout=success for tracking)
      let returnUrl = window.location.origin + "/manage?checkout=success";
      if (isFromTemplate && templateData) {
        const category = templateData.category || "General";
        const templateId = templateData.id || "1";
        returnUrl = window.location.origin + `/edit/${category}/${templateId}?checkout=success`;
        
        // Store complete template data in session for after subscription return
        // Include video properties for video templates
        sessionStorage.setItem('pendingTemplateData', JSON.stringify({
          src: templateData.src,
          category,
          cardSize: templateData.cardSize || "1024x1024",
          id: templateId,
          isVideo: templateData.isVideo || false,
          mediaType: templateData.mediaType || "image",
          firebaseUrl: templateData.firebaseUrl || null,
          // Preserve any additional template properties
          ...templateData
        }));
      }
      
      const { data } = await createStripeSubscription(price.stripe_price_id, returnUrl);
      
      // Track subscription initiation (actual conversion tracked on return from Stripe)
      fbPixel.trackEvent('InitiateCheckout', {
        content_name: `${plan.name} - yearly`,
        value: price.amount,
        currency: 'USD'
      });
      
      // Redirect to Stripe Checkout
      window.location.href = data.checkout_url;
    } catch (err) {
      console.error(err);
      showError("Failed to start subscription", true);
    } finally {
      setLoading(false);
    }
  }

  // NOTE: Preview plan is hidden from new users but kept for legacy users who already have it.
  // This function is retained in case we need to programmatically assign Preview tier.
  // eslint-disable-next-line no-unused-vars
  async function handlePreviewPlan() {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    try {
      setLoading(true);
      await createPreviewSubscription();
      
      // Track preview plan subscription
      fbPixel.trackSubscription('Preview Plan', 0, 'USD');
      
      // Check if we came from template selection and should redirect to editing
      if (isFromTemplate && templateData) {
        const category = templateData.category || "General";
        const templateId = templateData.id || "1";
        console.log('[Preview Plan] Redirecting to template editing:', { category, templateId, isVideo: templateData.isVideo });
        navigate(`/edit/${category}/${templateId}`, {
          state: {
            src: templateData.src,
            category,
            cardSize: templateData.cardSize || "1024x1024",
            isVideo: templateData.isVideo || false,
            mediaType: templateData.mediaType || "image",
            firebaseUrl: templateData.firebaseUrl || null,
            fromSubscription: true
          }
        });
      } else {
        // Normal preview subscription - go to manage page
        navigate("/manage");
      }
    } catch (err) {
      console.error("Failed to create preview subscription:", err);
      showError("Failed to start preview plan. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  // Handle credit pack purchase
  async function handleCreditPurchase(pack) {
    if (!user) {
      setSelectedCreditPack(pack);
      setShowAuthModal(true);
      return;
    }
    
    await processCreditPurchase(pack);
  }

  async function processCreditPurchase(pack) {
    setLoading(true);
    try {
      const returnUrl = window.location.origin + "/manage?credits=success";
      const cancelUrl = window.location.origin + "/subscribe?credits=canceled";
      
      const data = await purchaseCredits(pack.pack_type, returnUrl, cancelUrl);
      
      // Track credit purchase initiation
      fbPixel.trackEvent('InitiateCheckout', {
        content_name: pack.name,
        value: pack.price_cents / 100,
        currency: 'USD',
        content_type: 'credit_pack'
      });
      
      // Redirect to Stripe Checkout
      window.location.href = data.checkout_url;
    } catch (err) {
      console.error("Failed to purchase credits:", err);
      showError("Failed to start credit purchase. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  // When auth modal is closed and user is authenticated, proceed with subscription
  const handleAuthModalClose = async () => {
    setShowAuthModal(false);
    if (user && selectedPlan) {
      if (!selectedPlan.prices?.length) {
        // Free plan - handle directly here with template context
        handleSubscribe(selectedPlan);
      } else {
        // Paid plan - use normal subscription flow
        processSubscription(selectedPlan);
      }
    }
    if (user && selectedCreditPack) {
      processCreditPurchase(selectedCreditPack);
    }
    setSelectedPlan(null);
    setSelectedCreditPack(null);
  };

  // Calculate yearly savings (kept for potential future use)
  // eslint-disable-next-line no-unused-vars
  const calculateSavings = (plan) => {
    if (!plan.prices?.length) return 0;
    const monthlyPrice = plan.prices.find(p => p.interval === 'monthly')?.amount;
    const yearlyPrice = plan.prices.find(p => p.interval === 'yearly')?.amount;
    
    if (!monthlyPrice || !yearlyPrice) return 0;
    
    const yearlyWithMonthly = monthlyPrice * 12;
    return ((yearlyWithMonthly - yearlyPrice) / yearlyWithMonthly * 100).toFixed(0);
  };

  // Helper function to get the current price for a plan (yearly only)
  // eslint-disable-next-line no-unused-vars
  const getCurrentPrice = (plan) => {
    if (!plan.prices?.length) return "Free";
    const price = plan.prices.find(p => p.interval === 'yearly');
    return price ? price.amount.toFixed(2) : "N/A";
  };

  // Helper function to get features for a plan
  // eslint-disable-next-line no-unused-vars
  const getPlanFeatures = (plan) => {
    const features = [];
    Object.entries(plan.features).forEach(([key, value]) => {
      const formattedFeature = formatFeatureText(key, value);
      if (formattedFeature) {
        // Special case: watermark=false means "no watermark" which is a positive feature
        const isAvailable = key === 'watermark' 
          ? value === false  // No watermark = available/good
          : value === true || (typeof value === 'number' && value > 0);
        
        features.push({
          text: formattedFeature,
          available: isAvailable
        });
      }
    });
    return features;
  };

  // Show loading screen while checking subscription status
  // This prevents the pricing page from flashing before redirecting subscribed users
  if (checkingSubscription) {
    return (
      <>
        <SEO
          title="Pricing & Plans"
          description="Choose the perfect DreamableAI plan for your greeting card needs. Pay per card or subscribe for unlimited access. AI generation, premium templates, and no watermarks."
          path="/subscribe"
        />
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-500">Loading...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title="Pricing & Plans"
        description="Choose the perfect DreamableAI plan for your greeting card needs. Pay per card or subscribe for unlimited access. AI generation, premium templates, and no watermarks."
        path="/subscribe"
      />
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Hero Section */}
        <div className="relative overflow-hidden">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-[0.015] pointer-events-none">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%236366f1' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12">
          {/* Checkout Canceled Message */}
          {(checkoutCanceled || creditsCanceled) && (
            <div className="mb-8 max-w-xl mx-auto">
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 shadow-sm">
                <p className="text-amber-800 text-center text-sm">
                  <span className="font-medium">
                    {creditsCanceled ? 'Credit purchase canceled.' : 'Checkout canceled.'}
                  </span> No worries! You can try again when you're ready.
                </p>
              </div>
            </div>
          )}
          
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-3">
              {isFromTemplate ? (
                <>Unlock <span className="bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent">All Templates</span></>
              ) : isFromFeature === 'collaboration' ? (
                "Unlock Collaboration"
              ) : (
                <>Try <span className="bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent">Pro</span> Free for 3 Days</>
              )}
            </h1>
            <p className="text-base sm:text-lg text-slate-500 max-w-2xl mx-auto mb-8">
              {isFromTemplate
                ? "Start creating beautiful, personalized greeting cards today"
                : isFromFeature === 'collaboration'
                ? "Work together with friends and family on your cards"
                : "Create unlimited AI greeting cards with premium templates"
              }
            </p>
            
            {/* Billing Period Toggle */}
            <div className="flex justify-center items-center gap-4 mb-4">
              <span className={`text-base font-medium transition-colors ${billingPeriod === 'yearly' ? 'text-indigo-600' : 'text-slate-400'}`}>
                Yearly
              </span>
              <button
                onClick={() => setBillingPeriod(prev => prev === 'yearly' ? 'monthly' : 'yearly')}
                className="relative inline-flex h-7 w-14 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 bg-indigo-100"
              >
                <span
                  className={`inline-block h-6 w-6 transform rounded-full bg-indigo-600 shadow ring-0 transition duration-200 ease-in-out ${
                    billingPeriod === 'monthly' ? 'translate-x-7' : 'translate-x-0'
                  }`}
                />
              </button>
              <span className={`text-base font-medium transition-colors ${billingPeriod === 'monthly' ? 'text-indigo-600' : 'text-slate-400'}`}>
                Monthly
              </span>
            </div>
            {billingPeriod === 'yearly' && (
              <p className="text-sm text-green-600 font-medium">Save up to 37% with yearly billing!</p>
            )}
          </div>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">

        {/* Subscription Plans */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {plans.filter(plan => plan.prices?.length > 0).map((plan) => {
            const isPro = plan.name?.toLowerCase().includes('pro');
            const yearlyPrice = plan.prices.find(p => p.interval === 'yearly')?.amount;
            const monthlyPrice = plan.prices.find(p => p.interval === 'monthly')?.amount;
            const monthlyEquivalent = yearlyPrice ? (yearlyPrice / 12).toFixed(2) : null;
            const savings = monthlyPrice && yearlyPrice ? Math.round(((monthlyPrice * 12 - yearlyPrice) / (monthlyPrice * 12)) * 100) : 0;
            const emoji = isPro ? '👑' : '⭐';

            return (
              <div
                key={plan.name}
                className={`relative bg-white rounded-2xl shadow-xl overflow-hidden transition-all duration-300 hover:shadow-2xl active:scale-[0.99] ${
                  isPro ? 'ring-2 ring-indigo-500 shadow-indigo-500/10' : 'ring-1 ring-slate-200'
                }`}
              >
                {isPro && (
                  <div className="absolute -top-0 left-1/2 -translate-x-1/2 translate-y-0">
                    <div className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-xs font-bold px-4 py-1 rounded-b-lg shadow-lg">
                      Most Popular
                    </div>
                  </div>
                )}

                <div className="p-6 sm:p-8 pt-8">
                  <div className="mb-5 text-center">
                    <div className="text-3xl mb-2">{emoji}</div>
                    <h3 className="text-xl font-bold text-slate-900 mb-1">{plan.name}</h3>
                    <p className="text-slate-500 text-sm">
                      {isPro ? 'Unlimited cards, unlimited creativity' : '10 cards per month'}
                    </p>
                  </div>

                  <div className="mb-5 text-center">
                    {billingPeriod === 'yearly' ? (
                      <>
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="text-4xl font-bold text-slate-900">${monthlyEquivalent}</span>
                          <span className="text-slate-500">/mo</span>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">
                          ${yearlyPrice?.toFixed(2)}/year • Billed annually
                        </p>
                        {savings > 0 && (
                          <span className="inline-block mt-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                            Save {savings}%
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="text-4xl font-bold text-slate-900">${monthlyPrice?.toFixed(2)}</span>
                          <span className="text-slate-500">/mo</span>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">Billed monthly</p>
                      </>
                    )}
                  </div>

                  <div className="mb-4 text-center">
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">
                      🎁 3-day free trial
                    </span>
                  </div>

                  <button
                    onClick={() => handleSubscribe(plan)}
                    disabled={loading}
                    className={`w-full py-3 px-6 rounded-xl font-semibold text-base transition-all duration-200 ${
                      isPro
                        ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:from-indigo-600 hover:to-purple-600 shadow-lg shadow-indigo-500/25'
                        : 'bg-slate-800 text-white hover:bg-slate-700'
                    }`}
                  >
                    {loading ? 'Processing...' : 'Start Free Trial'}
                  </button>

                  <p className="text-center text-xs text-slate-400 mt-2">Cancel anytime</p>

                  <div className="mt-6 pt-5 border-t border-slate-100">
                    <ul className="space-y-2">
                      {(isPro ? [
                        'Unlimited greeting cards',
                        'Unlimited AI generation',
                        'All premium templates',
                        'No watermarks',
                        '10 collaborators/card',
                        'Schedule delivery',
                        'Priority support'
                      ] : [
                        '10 cards per month',
                        '20 AI photos/month',
                        'All premium templates',
                        'No watermarks',
                        '5 collaborators/card',
                        'Schedule delivery'
                      ]).map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                          <svg className={`w-4 h-4 flex-shrink-0 ${isPro ? 'text-indigo-500' : 'text-slate-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Pay As You Go Divider + Credit Packs */}
        {creditPacks.length > 0 && (
          <div className="mt-16">
            <div className="flex items-center gap-4 max-w-4xl mx-auto mb-10">
              <div className="flex-1 h-px bg-slate-200" />
              <div className="text-center">
                <p className="text-sm font-semibold text-slate-500 uppercase tracking-widest">Or, pay as you go</p>
                <p className="text-xs text-slate-400 mt-0.5">No subscription needed</p>
              </div>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {creditPacks.map((pack) => {
                const isBestValue = pack.pack_type === 'bundle_10';
                const emoji = isBestValue ? '🎁' : '💳';
                const pricePerCard = pack.credits > 1 ? (pack.price_cents / 100 / pack.credits).toFixed(2) : null;

                return (
                  <div
                    key={pack.pack_type}
                    className={`relative bg-white rounded-2xl shadow-xl overflow-hidden transition-all duration-300 hover:shadow-2xl active:scale-[0.99] ${
                      isBestValue ? 'ring-2 ring-indigo-500 shadow-indigo-500/10' : 'ring-1 ring-slate-200'
                    }`}
                  >
                    {isBestValue && (
                      <div className="absolute -top-0 left-1/2 -translate-x-1/2 translate-y-0">
                        <div className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-xs font-bold px-4 py-1 rounded-b-lg shadow-lg">
                          Best Value
                        </div>
                      </div>
                    )}

                    <div className="p-6 sm:p-8 pt-8">
                      <div className="mb-5 text-center">
                        <div className="text-3xl mb-2">{emoji}</div>
                        <h3 className="text-xl font-bold text-slate-900 mb-1">{pack.name}</h3>
                        <p className="text-slate-500 text-sm">
                          {pack.credits === 1 ? 'Perfect for a single occasion' : `${pack.credits} cards to use anytime`}
                        </p>
                      </div>

                      <div className="mb-5 text-center">
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="text-4xl font-bold text-slate-900">{pack.price_display}</span>
                        </div>
                        {pricePerCard && (
                          <p className="text-sm text-slate-500 mt-1">${pricePerCard} per card</p>
                        )}
                        {isBestValue && (
                          <span className="inline-block mt-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                            Save 25%
                          </span>
                        )}
                      </div>

                      <div className="mb-4 text-center">
                        <span className="inline-flex items-center px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-medium">
                          One-time purchase • Never expires
                        </span>
                      </div>

                      <button
                        onClick={() => handleCreditPurchase(pack)}
                        disabled={loading}
                        className={`w-full py-3 px-6 rounded-xl font-semibold text-base transition-all duration-200 ${
                          isBestValue
                            ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:from-indigo-600 hover:to-purple-600 shadow-lg shadow-indigo-500/25'
                            : 'bg-slate-800 text-white hover:bg-slate-700'
                        }`}
                      >
                        {loading ? 'Processing...' : `Buy ${pack.credits === 1 ? '1 Card' : `${pack.credits} Cards`}`}
                      </button>

                      <div className="mt-6 pt-5 border-t border-slate-100">
                        <ul className="space-y-2">
                          {[
                            `${pack.credits} greeting card${pack.credits > 1 ? 's' : ''}`,
                            'All premium templates',
                            'AI photo generation',
                            'No watermarks',
                            'Send via email or link',
                            'Credits never expire'
                          ].map((feature, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                              <svg className={`w-4 h-4 flex-shrink-0 ${isBestValue ? 'text-indigo-500' : 'text-slate-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        {/* How Your Free Trial Works */}
        <div className="mt-12 max-w-3xl mx-auto">
          <div className="bg-slate-50 rounded-2xl p-6 sm:p-8">
            <h3 className="font-semibold text-slate-900 mb-5 text-center">How Your Free Trial Works</h3>
            <div className="grid sm:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-2xl mb-2">1️⃣</div>
                <h4 className="font-medium text-slate-800 mb-1">Start Free</h4>
                <p className="text-sm text-slate-600">Sign up and get full access to all features for 3 days</p>
              </div>
              <div>
                <div className="text-2xl mb-2">2️⃣</div>
                <h4 className="font-medium text-slate-800 mb-1">Create Freely</h4>
                <p className="text-sm text-slate-600">Make unlimited cards with AI generation and premium templates</p>
              </div>
              <div>
                <div className="text-2xl mb-2">3️⃣</div>
                <h4 className="font-medium text-slate-800 mb-1">Cancel Anytime</h4>
                <p className="text-sm text-slate-600">No charge if you cancel before your trial ends</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Trust Section */}
        <div className="mt-16">
          <div className="text-center mb-8">
            <p className="text-sm font-medium text-slate-400 uppercase tracking-wider">
              Trusted by creators worldwide
            </p>
          </div>
          
          {/* Feature Highlights */}
          <div className="grid sm:grid-cols-3 gap-6">
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                </svg>
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">Secure payments</h3>
              <p className="text-sm text-slate-500">256-bit SSL encryption via Stripe</p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">Cancel anytime</h3>
              <p className="text-sm text-slate-500">No contracts, no commitments</p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
                </svg>
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">24/7 Support</h3>
              <p className="text-sm text-slate-500">We're here to help you succeed</p>
            </div>
          </div>
        </div>
        
        {/* FAQ-style info */}
        <div className="mt-12 max-w-2xl mx-auto">
          <div className="bg-slate-50 rounded-2xl p-6 sm:p-8">
            <h3 className="font-semibold text-slate-900 mb-4">Frequently Asked Questions</h3>
            <div className="space-y-4 text-sm text-slate-600">
              <div>
                <p className="font-medium text-slate-800 mb-1">When will I be charged?</p>
                <p>Your 3-day free trial starts immediately. You won't be charged until your trial ends. Cancel anytime before then at no cost.</p>
              </div>
              <div>
                <p className="font-medium text-slate-800 mb-1">What's included in the Pro Plan?</p>
                <p>Everything! Unlimited greeting cards, AI image generation, all premium templates, no watermarks, collaboration features, and scheduled delivery.</p>
              </div>
              <div>
                <p className="font-medium text-slate-800 mb-1">Do card credits expire?</p>
                <p>No! One-time card credits never expire. Use them whenever you need to create a greeting card.</p>
              </div>
              <div>
                <p className="font-medium text-slate-800 mb-1">Can I cancel anytime?</p>
                <p>Yes! Cancel your subscription at any time. You'll keep access until the end of your billing period.</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Institution/Enterprise Section */}
        <div className="mt-12 max-w-2xl mx-auto">
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-6 sm:p-8 border border-indigo-100">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-sm flex-shrink-0">
                <svg className="w-7 h-7 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
                </svg>
              </div>
              <div className="text-center sm:text-left flex-1">
                <h3 className="font-semibold text-slate-900 text-lg mb-1">Schools, Organizations & Enterprise</h3>
                <p className="text-sm text-slate-600">
                  Need volume pricing for your institution, school, or team? We offer custom plans with dedicated support and special features.
                </p>
              </div>
              <a
                href="mailto:info@hungryai.com?subject=DreamableAI%20Institution%20Plan%20Inquiry"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-indigo-600 font-semibold text-sm rounded-xl border border-indigo-200 hover:bg-indigo-50 hover:border-indigo-300 transition-all duration-200 shadow-sm whitespace-nowrap"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                </svg>
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Auth Modal - default to register for new users choosing a plan */}
      {showAuthModal && (
          <AuthModal
            onClose={handleAuthModalClose}
            initialTab="register"
          />
        )}
      </div>
    </>
  );
}
